/**
 * Voice modal: full-bleed overlay inside the chat panel.
 *
 * Lifecycle:
 *  1. Operator clicks the mic button in the composer -> modal opens
 *     in `idle` with a single breathing orb labelled "tap to call".
 *  2. Operator taps the orb -> `startVoiceSession` walks the state
 *     machine.
 *  3. Live state shows a soft audio-bars indicator under the title
 *     and the orb pulses with concentric rings.
 *  4. The navy "end call" pill is always visible during connecting/
 *     live so the operator can stop the call at any moment.
 *  5. Errors render as a calm body line; the modal stays open so the
 *     operator can retry on their own terms.
 *  6. The "Back to chat" link in the header always works (it ends
 *     the session if one is alive).
 *
 * Visual intent: a phone call with a person, not a Siri overlay.
 * The cream wash + single orb + audio bars feel premium and quiet
 * even at the moment of connection.
 *
 * The modal renders inside the same shadow root as the panel, so
 * customer CSS cannot reach in. Animations live in `styles.ts` so
 * we can pause them via class toggles when the call is idle.
 */

import { useEffect, useRef, useState } from "preact/hooks";
import type { tFor } from "../i18n";
import { MicIcon } from "../icons";
import type { WidgetBootConfig } from "../types";
import { type VoiceSessionHandle, type VoiceState, startVoiceSession } from "./voice-session";

interface Props {
  boot: WidgetBootConfig;
  t: ReturnType<typeof tFor>;
  onClose: () => void;
}

export function VoiceModal({ boot, t, onClose }: Props) {
  const [state, setState] = useState<VoiceState>({ kind: "idle" });
  const handleRef = useRef<VoiceSessionHandle | null>(null);

  // Tear down on unmount. The handle's `end()` is idempotent, so a
  // close click followed by an unmount cannot double-end.
  useEffect(() => {
    return () => {
      void handleRef.current?.end();
    };
  }, []);

  async function start() {
    if (state.kind !== "idle" && state.kind !== "ended" && state.kind !== "error") {
      return;
    }
    setState({ kind: "requesting_permission" });
    handleRef.current = await startVoiceSession(boot, {
      onState: (next) => setState(next),
    });
  }

  async function end() {
    await handleRef.current?.end();
  }

  async function close() {
    await handleRef.current?.end();
    onClose();
  }

  const showStartOrb = state.kind === "idle" || state.kind === "ended" || state.kind === "error";
  const showEndButton =
    state.kind === "connecting" || state.kind === "requesting_permission" || state.kind === "live";
  const isLive = state.kind === "live";
  const isConnecting = state.kind === "connecting" || state.kind === "requesting_permission";

  return (
    <div class="rh-voice-modal" role="dialog" aria-label={t.voice_modal_title}>
      <div class="rh-voice-header">
        <button
          type="button"
          class="rh-voice-back"
          aria-label={t.voice_close_aria}
          onClick={() => void close()}
        >
          <BackIcon />
          {t.voice_back_to_text}
        </button>
        <span class={`rh-voice-status${isLive ? " live" : ""}`}>
          <span class="rh-voice-status-dot" aria-hidden="true" />
          {renderStatusLabel(state, t)}
        </span>
      </div>
      <div class="rh-voice-body">
        <h2 class="rh-voice-title">{t.voice_modal_title}</h2>
        {isLive ? (
          <span class="rh-voice-bars" aria-hidden="true">
            <span />
            <span />
            <span />
            <span />
            <span />
            <span />
          </span>
        ) : null}
        <div
          class={`rh-voice-orb${isLive || isConnecting ? " rh-voice-orb-active" : ""}`}
          aria-hidden="true"
        >
          <div class="rh-voice-orb-halo" />
          <div class="rh-voice-orb-ring rh-voice-orb-ring-1" />
          <div class="rh-voice-orb-ring rh-voice-orb-ring-2" />
          <div class="rh-voice-orb-ring rh-voice-orb-ring-3" />
          {showStartOrb ? (
            <button
              type="button"
              class="rh-voice-orb-core"
              aria-label={t.voice_start_label}
              onClick={() => void start()}
            >
              <MicIcon />
            </button>
          ) : (
            <div class={`rh-voice-orb-core${isLive ? " live" : ""}`}>
              <MicIcon />
            </div>
          )}
        </div>
        <p class="rh-voice-message">{renderBody(state, t)}</p>
        {showStartOrb ? <p class="rh-voice-hint">{t.voice_start_label}</p> : null}
        {showEndButton ? (
          <button type="button" class="rh-voice-end" onClick={() => void end()}>
            <span class="rh-voice-end-square" aria-hidden="true" />
            {t.voice_end}
          </button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Pick the body line for the current state. Keep the layout still:
 * the modal's height never jumps, errors render as the body line so
 * the operator gets context without a popping toast.
 */
function renderBody(state: VoiceState, t: ReturnType<typeof tFor>): string {
  if (state.kind === "idle" || state.kind === "ended") return t.voice_idle_body;
  if (state.kind === "requesting_permission") return t.voice_connecting;
  if (state.kind === "connecting") return t.voice_connecting;
  if (state.kind === "live") return t.voice_live;
  if (state.kind === "error" && state.reason === "permission_denied") {
    return t.voice_permission_denied;
  }
  return t.voice_error_generic;
}

/**
 * Tiny status label rendered in the modal header (uppercase, tracked
 * out). Mirrors the body-line semantics but in a single word so the
 * operator can scan the call state from the corner of the eye.
 */
function renderStatusLabel(state: VoiceState, t: ReturnType<typeof tFor>): string {
  if (state.kind === "live") return t.voice_listening;
  if (state.kind === "connecting" || state.kind === "requesting_permission") {
    return t.voice_connecting;
  }
  if (state.kind === "ended") return t.voice_idle_body.split(",")[0] ?? "";
  return "";
}

/**
 * Tiny chevron-left icon used in the "back to chat" pill. Hand-rolled
 * to avoid pulling in a second size variant of the chevron SVG.
 */
function BackIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="15 6 9 12 15 18" />
    </svg>
  );
}
